import { mergeRoutes, sharedRoutes } from '@shared/routes.js'
import { createRouter, createWebHistory, isNavigationFailure, NavigationFailureType } from 'vue-router'
import store from '@/store/index'
import { useProactiveTriggersStore } from '@shared/stores/proactiveTriggers.js'
import { useExperimentsStore } from '@shared/stores/experiments.js'
import { useOverlaysStore } from '@/stores/overlays.js'
import { useClientStore } from '@shared/stores/client.js'
import { signUpExperimentName, useSignUpExperiment } from '@shared/composables/experiment.js'
import { areRouteComponentsReplaced, replaceRouteComponents } from '@/utils/route.js'
import { identify, page } from './plugins/analytics'
import { isTest } from '@shared/utils/environment.js'
import { nextTick } from 'vue'
import { useDrawers } from '@/composables/drawers.js'

const ClosetPageSection = () => import('@/components/closet/ClosetPageSection.vue')
const FeedPage = () => import('@/components/community/feed/FeedPage.vue')
const FeedByTag = () => import('@/components/community/feed/FeedByTag.vue')
const AccountOverview = () => import('@/components/account/AccountOverview.vue')
const BoutiqueCheckin = () => import('@/components/account/BoutiqueCheckin.vue')
const StyleQuizSuccess = () => import('@/components/styleQuiz/StyleQuizSuccess.vue')

export const routes = mergeRoutes(sharedRoutes, [
  {
    path: '/style-profile',
    component: () => import('@/components/styleProfile/StyleProfile.vue'),
    meta: { requiresAuth: true },
    name: 'style-profile'
  },
  {
    path: '/closet',
    component: () => import('@/components/closet/ClosetPage.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'closet-sections',
        component: () => import('@/components/closet/MyCloset.vue')
      },
      {
        path: ':name/:id',
        name: 'closet-section',
        component: ClosetPageSection,
        meta: { requiresAuth: true, requiresStyleProfile: true }
      },
      {
        path: 'u/:name/:id',
        name: 'closet-section-anonymous',
        component: ClosetPageSection,
        meta: { allowAnonymous: true }
      },
      {
        path: 'detail/:name/:id',
        name: 'closet-detail',
        component: () => import('@/components/closet/ClosetPageDetail.vue'),
        props: true,
        meta: {
          allowAnonymous: true,
          trackSharing: true
        }
      },
      {
        path: 'detail/:name/:id/photos',
        meta: {
          hideMobileNavBar: true,
          allowAnonymous: true
        },
        name: 'closet-detail-member-photos',
        component: () => import('@/components/closet/ClosetMemberPhotos.vue')
      },
      {
        path: 'brand/:name/:brandId',
        name: 'brand',
        component: () => import('@/components/closet/ClosetPageBrand.vue'),
        meta: {
          allowAnonymous: true,
          trackSharing: true
        }
      },
      {
        path: 'browse/:section/:subsection/:sectionId?',
        name: 'browse',
        component: () => import('@/components/global/browse/BrowsePage.vue'),
        meta: {
          allowAnonymous: true,
          trackSharing: true
        }
      },
      {
        path: 'collections/client/:clientId',
        name: 'client-collections',
        component: () => import('@/components/collections/ClientCollections.vue'),
        meta: {
          allowAnonymous: true
        }
      },
      {
        path: 'collections/generic/:filterType/:filter/:header/:minItems',
        name: 'generic-collections',
        component: () => import('@/components/collections/GenericCollections.vue')
      },
      {
        path: 'collections/detail/:id',
        name: 'collection',
        component: () => import('@/components/collections/CollectionPage.vue'),
        meta: {
          allowAnonymous: true,
          trackSharing: true
        }
      }
    ]
  },
  {
    path: '/home',
    name: 'home',
    redirect: () => {
      return '/closet'
    }
  },
  {
    path: '/my-stuff',
    name: 'my-stuff',
    component: () => import('@/views/MyStuffPage.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/style-games',
    redirect: '/my-stuff#style-games',
  },
  {
    path: '/fedex-opt-in',
    name: 'fedex-opt-in',
    beforeEnter: async () => {
      try {
        const clientStore = useClientStore()
        await clientStore.fedExOptIn()
      } finally {
        router.push({ name: 'closet-sections' })
      }
    },
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/mylatestcase',
    redirect: { name: 'closet-sections', params: { requiresAuth: true }, query: { myCase: 'core', latestCase: 'true' } }
  },
  {
    path: '/stylistcase',
    redirect: { name: 'closet-sections', params: { requiresAuth: true }, query: { myCase: 'surprise-case' } }
  },
  {
    path: '/chatbot',
    name: 'chatbot',
    beforeEnter: async () => {
      try {
        const { chatbotDrawer } = useDrawers()
        await nextTick()
        chatbotDrawer.open()
      } finally {
        router.push({ name: 'closet-sections' })
      }
    },
  },
  {
    path: '/community',
    component: () => import('@/components/community/CommunityPage.vue'),
    meta: {
      requiresAuth: true,
      allowAnonymous: true
    },
    children: [
      {
        path: '',
        name: 'feed',
        component: FeedPage
      },
      {
        path: ':source',
        name: 'feed-by-source',
        component: FeedPage,
        meta: {
          allowAnonymous: false
        }
      },
      {
        path: 'tag/:tagId/:tag',
        name: 'feed-item-tag-id',
        component: FeedByTag,
        meta: {
          allowAnonymous: false,
          hideMobileNavBar: true
        }
      },
      {
        path: 'tag/:tag',
        name: 'feed-item-tag',
        component: FeedByTag,
        meta: {
          allowAnonymous: false,
          hideMobileNavBar: true
        }
      },
      {
        path: 'feeditem/:id',
        name: 'feed-item',
        component: () => import('@/components/community/feed/FeedItemDeepLinkPage.vue'),
        meta: {
          allowAnonymous: false,
          requiresStyleProfile: true,
          hideMobileNavBar: true
        }
      },
      {
        path: 'feeditem/:id/likes',
        name: 'feed-item-likes',
        component: () => import('@/components/community/feed/ViewLikesMobile.vue'),
        meta: {
          allowAnonymous: false,
          requiresStyleProfile: true,
          hideMobileNavBar: true
        }
      },
      {
        path: 'look/:id',
        name: 'look',
        component: () => import('@/components/community/feed/LookDeeplinkPage.vue'),
        meta: {
          allowAnonymous: false,
          requiresStyleProfile: true,
          hideMobileNavBar: true
        }
      },
      {
        path: 'edit-comment',
        name: 'edit-comment',
        component: () => import('@/components/community/feed/update/EditCommentPage.vue'),
        meta: {
          allowAnonymous: false,
          requiresStyleProfile: true,
          hideMobileNavBar: true
        }
      },
      {
        path: 'share',
        component: () => import('@/components/community/sharelook/ShareALookPage.vue'),
        meta: {
          allowAnonymous: false,
          requiresStyleProfile: true,
          hideMobileNavBar: true
        },
        name: 'share-a-look'
      },
      {
        path: 'edit',
        meta: {
          allowAnonymous: false,
          requiresStyleProfile: true,
          hideMobileNavBar: true
        },
        name: 'edit-look',
        component: () => import('@/components/community/sharelook/EditLook.vue')
      },
      {
        path: 'profile',
        component: () => import('@/components/community/profile/ProfileRouter.vue'),
        meta: {
          allowAnonymous: true,
          hideMobileNavBar: true,
          trackSharing: true
        },
        children: [
          {
            path: 'edit',
            name: 'edit-profile',
            meta: { hideMobileNavBar: true },
            component: () => import('@/components/community/profile/update/EditProfile.vue')
          },
          {
            path: ':id?/:tab?',
            name: 'profile',
            meta: { hideMobileNavBar: true },
            component: () => import('@/components/community/profile/ProfilePage.vue')
          }
        ]
      }
    ]
  },
  {
    path: '/account',
    component: () => import('@/components/account/AccountPage.vue'),
    meta: {
      requiresAuth: true,
      hideBanners: 'children'
    },
    children: [
      {
        path: '',
        name: 'account',
        component: AccountOverview
      },
      {
        path: 'billing',
        name: 'account-billing',
        component: AccountOverview,
        children: [
          {
            path: 'cancel-confirm',
            redirect: '/account/membership'
          }
        ]
      },
      {
        path: 'delete',
        name: 'delete-account',
        component: AccountOverview
      },
      {
        path: 'update-address',
        name: 'update-address',
        component: () => import('@/components/account/ShippingPage.vue')
      },
      {
        path: 'boutique-checkin',
        name: 'boutique-checkin',
        component: BoutiqueCheckin
      },
      {
        path: 'boutique-checkin/:kiosk',
        name: 'boutique-checkin-direct',
        component: BoutiqueCheckin
      },
      {
        path: 'gift-cards',
        name: 'gift-cards',
        component: () => import('@/components/account/giftcards/GiftCardsPage.vue')
      },
      {
        path: 'rewards',
        name: 'rewards',
        component: () => import('@/components/account/RewardsPage.vue')
      },
      {
        path: 'notifications',
        component: () => import('@/components/account/notifications/AccountNotifications.vue'),
        children: [
          {
            path: 'community',
            name: 'notification-settings-feed',
            component: () => import('@/components/account/notifications/FeedSettings.vue'),
            meta: { hideMobileNavBar: true }
          },
          {
            path: 'announcements',
            name: 'notification-settings-announcements',
            component: () => import('@/components/account/notifications/AnnouncementSettings.vue'),
            meta: { hideMobileNavBar: true }
          },
          {
            path: '',
            name: 'notification-settings-membership',
            component: () => import('@/components/account/notifications/MembershipSettings.vue'),
            meta: { hideMobileNavBar: true }
          }
        ]
      },
      {
        path: 'membership',
        component: () => import('@/components/account/membership/new/wrappers/AccountMembershipWrapper.vue'),
        children: [
          {
            path: '',
            name: 'membership-overview',
            component: () => import('@/components/account/membership/new/wrappers/MembershipOverviewWrapper.vue'),
            meta: { hideMobileNavBar: true, allowPendingCancel: true }
          },
          {
            path: 'pause-or-cancel',
            name: 'pause-or-cancel',
            component: () => import('@/views/account/PauseOrCancelPage.vue'),
            meta: { requiresSubscribed: true, hideMobileNavBar: true }
          },
          {
            path: 'pause',
            name: 'pause',
            component: () => import('@/components/account/membership/new/wrappers/PauseWrapper.vue'),
            meta: { requiresSubscribed: true, hideMobileNavBar: true }
          },
          {
            path: 'extend-pause',
            name: 'extend-pause',
            component: () => import('@/views/account/ExtendPausePage.vue'),
            meta: { requiresSubscribed: true, hideMobileNavBar: true }
          },
          {
            path: 'change-plan',
            name: 'change-plan',
            component: () => import('@/components/account/membership/new/wrappers/ChangePlanWrapper.vue'),
            meta: { requiresSubscribed: true, hideMobileNavBar: true }
          },
          {
            path: 'cancel',
            name: 'cancel',
            component: () => import('@/components/account/membership/new/wrappers/CancelWrapper.vue'),
            meta: { requiresSubscribed: true, hideMobileNavBar: true }
          },
          {
            path: 'cancel-benefits',
            name: 'cancel-benefits',
            component: () => import('@/views/account/CancelBenefitsPage.vue'),
            meta: { requiresSubscribed: true, hideMobileNavBar: true }
          },
          {
            path: 'cancel-confirmation',
            name: 'cancel-confirmation',
            component: () => import('@/views/account/CancelConfirmationPage.vue'),
            meta: { hideMobileNavBar: true }
          },
          {
            path: 'confirm-plan-change',
            name: 'confirm-plan-change',
            component: () => import('@/components/account/membership/ConfirmPlanChange.vue'),
            meta: { requiresSubscribed: true, hideMobileNavBar: true, allowPendingCancel: true }
          },
          {
            path: 'prepay',
            redirect: { name: 'change-plan' }
          },
          {
            path: 'resume',
            name: 'resume',
            component: () => import('@/components/account/membership/ResumeMembership.vue'),
            meta: { requiresSubscribed: true, hideMobileNavBar: true, allowPendingCancel: true }
          },
          {
            path: 'rejoin',
            name: 'rejoin',
            component: () => import('@/components/account/membership/RejoinPage.vue'),
            meta: { requiresSubscribed: true, hideMobileNavBar: true }
          }
        ]
      },
      {
        path: 'pending-cancel',
        name: 'pending-cancel',
        component: () => import('@/components/account/PendingCancel.vue')
      }
    ]
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('@/components/subscribe/SignUp.vue'),
    meta: {
      hideIntercomLauncher: 'children'
    },
    children: [
      {
        path: 'membership',
        meta: { requiresAuth: true },
        component: () => import('@/components/subscribe/MembershipPage.vue'),
        children: [
          {
            path: 'select-plan',
            component: () => import('@/components/subscribe/SelectPlan.vue'),
            name: 'select-plan',
            meta: {
              requiresUnsubscribed: true,
              hideNavBar: true,
              hideBanners: true,
              experiment: {
                name: signUpExperimentName,
                variant: 'new',
                whenInactive: true,
                redirect: { name: 'sign-up-plan-choice' }
              }
            },
            // used by SelectPlan to determine if we should show breadcrumbs
            beforeEnter: (to, from, next) => {
              if (from.name === 'account') { to.meta.from = from }
              next()
            }
          },
          {
            path: 'subscribe',
            component: () => import('@/components/subscribe/SubscribePage.vue'),
            name: 'subscribe',
            meta: {
              requiresUnsubscribed: true,
              hideNavBar: true,
              hideBanners: true,
              experiment: {
                name: signUpExperimentName,
                variant: 'new',
                whenInactive: true,
                redirect: { name: 'sign-up-checkout' }
              }
            }
          }
        ]
      },
      {
        path: 'success',
        name: 'subscribe-success',
        component: StyleQuizSuccess
      }
    ]
  },
  {
    path: '/login',
    component: () => import('@/components/auth/LoginPage.vue'),
    name: 'login',
    meta: { allowAnonymous: true }
  },
  {
    path: '/event-signup',
    name: 'event-signup',
    redirect: '/style-quiz/create-account',
    meta: { allowAnonymous: true }
  },
  {
    path: '/create-account',
    name: 'create-account',
    redirect: '/style-quiz/create-account',
    meta: { allowAnonymous: true }
  },
  {
    path: '/review',
    component: () => import('@/components/review/ReviewWrapper.vue'),
    meta: { requiresAuth: true, allowPendingCancel: true },
    children: [
      {
        path: '',
        name: 'review-select',
        meta: { hideMobileNavBar: true, allowPendingCancel: true },
        component: () => import('@/components/review/ReviewSelect.vue')
      },
      {
        path: 'item/:reviewItemIds/:reviewItemIndex?',
        name: 'review-item',
        meta: { hideMobileNavBar: true, allowPendingCancel: true },
        component: () => import('@/components/review/ReviewItem.vue')
      },
      {
        path: 'edit-photos',
        name: 'edit-review-photos',
        meta: { hideMobileNavBar: true, allowPendingCancel: true },
        component: () => import('@/components/review/ReviewEditPhotos.vue')
      },
      {
        path: 'swap',
        name: 'review-optional-swap-select',
        meta: { hideMobileNavBar: true, allowPendingCancel: true },
        component: () => import('@/components/review/ReviewOptionalSwapSelect.vue')
      },
      {
        path: 'success',
        name: 'review-success',
        component: () => import('@/components/review/ReviewSuccess.vue')
      }
    ]
  },
  {
    path: '/swap',
    component: () => import('@/components/review/SwapWrapper.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'swap-select',
        meta: { hideMobileNavBar: true },
        component: () => import('@/components/review/SwapSelect.vue')
      },
      {
        path: 'confirm',
        name: 'swap-confirm',
        meta: { hideMobileNavBar: true },
        component: () => import('@/components/review/SwapConfirm.vue')
      },
      {
        path: 'success',
        name: 'swap-success',
        component: () => import('@/components/review/SwapSuccess.vue')
      }
    ]
  },
  {
    path: '/quarterly-return',
    component: () => import('@/components/quarterlyReturn/QuarterlyReturnWrapper.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'quarterly-return-select',
        meta: { hideMobileNavBar: true },
        component: () => import('@/components/quarterlyReturn/QuarterlyReturnSelect.vue')
      },
      {
        path: 'confirm',
        name: 'quarterly-return-confirm',
        meta: { hideMobileNavBar: true },
        component: () => import('@/components/quarterlyReturn/QuarterlyReturnConfirm.vue')
      }
    ]
  },
  {
    path: '/referral',
    name: 'referral',
    component: () => import('@/components/referral/ReferralPage.vue'),
    meta: { requiresAuth: true, hideBanners: true }
  },
  {
    path: '/styleprofile/intro',
    redirect: '/signup/intro'
  },
  {
    path: '/search',
    component: () => import('@/components/search/SearchPage.vue'),
    meta: { requiresAuth: true },
    name: 'search'
  },
  {
    path: '/search/results',
    component: () => import('@/components/search/SearchResultsPage.vue'),
    meta: { requiresAuth: true },
    name: 'searchResults'
  },
  {
    path: '/sign-up',
    name: 'sign-up',
    component: () => import('@/views/SignUpPage.vue'),
    meta: {
      fullScreen: 'children',
      experiment: {
        name: signUpExperimentName,
        variant: 'new',
        children: true
      }
    },
    children: [
      {
        path: '',
        name: 'sign-up-intro',
        component: () => import('@/views/signUp/IntroScreen.vue')
      },
      {
        path: 'create-account',
        name: 'sign-up-create-account',
        component: () => import('@/views/signUp/CreateAccountScreen.vue')
      },
      {
        path: 'style-game',
        name: 'sign-up-style-game',
        component: () => import('@/views/signUp/StyleGameScreen.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'hard-nos',
        name: 'sign-up-hard-nos',
        component: () => import('@/views/signUp/HardNosScreen.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'sizing',
        name: 'sign-up-sizing',
        component: () => import('@/views/signUp/SizingScreen.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'clothes-sizing',
        name: 'sign-up-clothes-sizing',
        component: () => import('@/views/signUp/ClothesSizingScreen.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'events',
        name: 'sign-up-events',
        component: () => import('@/views/signUp/EventsScreen.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'reasons',
        name: 'sign-up-reasons',
        component: () => import('@/views/signUp/ReasonsScreen.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'plan-choice-survey',
        name: 'sign-up-plan-choice-survey',
        component: () => import('@/views/signUp/PlanChoiceSurveyScreen.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'plan-choice',
        name: 'sign-up-plan-choice',
        component: () => import('@/views/signUp/PlanChoiceScreen.vue'),
        meta: {
          requiresAuth: true,
          requiresUnsubscribed: false
        }
      },
      {
        path: 'checkout',
        name: 'sign-up-checkout',
        component: () => import('@/views/signUp/CheckoutScreen.vue'),
        meta: {
          requiresAuth: true,
          requiresUnsubscribed: false
        }
      },
      {
        // Special route to navigate to the first incomplete
        // style quiz screen. Handled in sign up navigation store.
        path: 'style-quiz',
        name: 'sign-up-style-quiz',
        meta: {
          requiresAuth: true
        }
      }
    ]
  },
  {
    path: '/style-quiz',
    component: () => import('@/components/styleQuiz/StyleQuiz.vue'),
    name: 'Style Quiz',
    meta: { fullScreen: 'children' },
    children: [
      {
        path: 'intro',
        name: 'style-quiz-intro',
        component: () => import('@/components/styleQuiz/StyleQuizIntro.vue'),
        meta: {
          experiment: {
            name: signUpExperimentName,
            variant: 'new',
            whenInactive: true,
            redirect: { name: 'sign-up-intro' }
          }
        }
      },
      {
        path: 'create-account',
        name: 'style-quiz-create-account',
        component: () => import('@/components/styleQuiz/StyleQuizCreateAccount.vue')
      },
      {
        path: 'style-clusters/:cluster',
        name: 'style-quiz-style-clusters',
        component: () => import('@/components/styleQuiz/StyleQuizStyleClusters.vue'),
        meta: {
          requiresAuth: true,
          experiment: {
            name: signUpExperimentName,
            variant: 'new',
            whenInactive: true,
            redirect: { name: 'sign-up-style-quiz' }
          }
        }
      },
      {
        path: 'brands',
        name: 'style-quiz-brands',
        component: () => import('@/components/styleQuiz/StyleQuizBrands.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'measurements',
        name: 'style-quiz-measurements',
        component: () => import('@/components/styleQuiz/StyleQuizMeasurements.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'due-date',
        name: 'style-quiz-due-date',
        component: () => import('@/components/styleQuiz/StyleQuizDueDate.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'sizes',
        name: 'style-quiz-sizes',
        component: () => import('@/components/styleQuiz/StyleQuizSizes.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'birthday',
        name: 'style-quiz-birthday',
        component: () => import('@/components/styleQuiz/StyleQuizBirthday.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'membership',
        name: 'style-quiz-choose-membership',
        component: () => import('@/components/styleQuiz/StyleQuizChooseMembership.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'prepay-plan',
        name: 'style-quiz-prepay-plan',
        component: () => import('@/components/styleQuiz/StyleQuizPrepayPlan.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'success',
        name: 'style-quiz-success',
        component: StyleQuizSuccess,
        meta: { requiresAuth: true }
      }
    ]
  },
  {
    path: '/membership',
    redirect: '/signup/membership'
  },
  {
    path: '/signup/intro',
    redirect: '/style-quiz/intro'
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404' // Shared route.
  }
])

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior (to, from, savedPosition) {
    // Disable scroll behavior in tests to avoid flakiness.
    if (isTest) {
      return false
    }
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    }
    if (history.state?.noScroll) {
      return false
    }
    const scrollOptions = { left: 0, top: 0, behavior: 'instant' }
    if (savedPosition) {
      // We only get a `savedPosition` value if we're navigating backwards.
      // First check if we navigated while an overlay was open and restore
      // the scroll position we saved before the overlay was opened.
      const savedScrollPosition = useOverlaysStore().savedScrollPositions.pop()
      if (savedScrollPosition) {
        scrollOptions.top = savedScrollPosition
      } else {
        scrollOptions.top = savedPosition.top
      }
    }
    return scrollOptions
  }
})

const proactiveTriggersSet = router.beforeEach(async () => {
  useProactiveTriggersStore()
  proactiveTriggersSet()
})

// Sign Up experiment route updates
const signUpRoutesUpdated = router.beforeEach(async (to) => {
  if (isTest) {
    signUpRoutesUpdated()
    return
  }
  await useExperimentsStore().untilExperimentsLoaded({ throwOnTimeout: false })
  const routeReplacements = useSignUpExperiment().routeReplacements.value
  if (!routeReplacements) {
    signUpRoutesUpdated()
    return
  }
  const registeredRoutes = router.getRoutes()
  if (areRouteComponentsReplaced(routeReplacements, registeredRoutes)) {
    signUpRoutesUpdated()
    return
  }
  replaceRouteComponents(routeReplacements, routes, router)
  signUpRoutesUpdated()
  return to.fullPath // Re-navigate to pick up changes.
})

router.beforeEach(async (to, from) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const allowAnonymous = to.matched.some(record => record.meta.allowAnonymous)

  const hasQueryParams = route => !!Object.keys(route.query).length
  const hasUtmParams = route => ['utm_source', 'utm_medium', 'utm_campaign'].some(key => key in route.query)

  // Capture and store utm campaign attributes
  if (hasUtmParams(to)) {
    ['utm_campaign', 'utm_source', 'utm_medium'].forEach(key => {
      const value = to.query[key]
      value ? sessionStorage.setItem(key, value) : sessionStorage.removeItem(key)
    })
  }

  if (to.path.includes('search') && !parseInt(import.meta.env.VITE_SEARCH)) {
    router.push({ name: 'closet-sections' })
  }

  // If auth is required, check if the user is authenticated.
  if (requiresAuth && !store.state.client.loaded) {
    try {
      await store.dispatch('client/getClient')
    } catch (err) {
      if (!allowAnonymous) {
        if (from.name === 'closet-detail' && to.name === 'closet-sections') {
          window.location.href = window.location.origin + window.location.search
          return
        }
        store.commit('global/SET_INITIAL_ROUTE', to.name)
        return ({ name: 'login', query: { next: to.fullPath } })
      }
    }
  }

  // Redirect if the user is not in the required experiment variant.
  const experiment = to.meta.experiment ?? to.matched.find(
    record => record.meta.experiment?.children === true
  )?.meta.experiment
  if (experiment) {
    const { whenInactive = false, name, redirect = null, variant = null } = experiment
    const experimentsStore = useExperimentsStore()
    const { isActiveExperiment, isActiveVariant, untilExperimentsLoaded } = experimentsStore
    await untilExperimentsLoaded({ throwOnTimeout: false })
    const isActive = variant ? isActiveVariant(name, variant) : isActiveExperiment(name)
    if (whenInactive ? isActive : !isActive) {
      const redirectTo = redirect ?? { name: experimentsStore.isAuthenticated ? 'closet-sections' : 'home' }
      redirectTo.query = { ...redirectTo.query, ...to.query } // Preserve query params
      return redirectTo
    }
  }

  // Black-hole page for OOC pending-cancel users
  if (store.state.client.membershipStatus === 'items_out_pending_cancel') {
    const allowedRoutes = ['pending-cancel', 'account', 'returns']
    const isAllowedRoute = allowedRoutes.includes(to.name)
    if (!isAllowedRoute && !to.meta.allowPendingCancel) {
      if (isAllowedRoute) {
        return
      }
      router.push({ name: 'pending-cancel' })
    }
  }

  const client = store.state.client
  identify(client)
  page(client, to.fullPath)

  if (to.path.includes('community/profile') && !to.params.id && to.name !== 'commmunity-profile') {
    return ({ name: to.name, params: { ...to.params, id: client.id } })
  }

  // Propagate query params throughout the style quiz for referral
  if (from.name?.startsWith('style-quiz') && to.name.startsWith('style-quiz') && hasQueryParams(from) && !hasQueryParams(to)) {
    return { name: to.name, params: to.params, query: { r: from.query.r, s: from.query.s, prepayGroup: from.query.prepayGroup } }
  }

  if (to.redirectedFrom?.name?.includes('event-signup') && !to.query.isEvent) {
    return ({ name: to.name, query: { r: from.query.r, s: from.query.s, isEvent: true } })
  }

  if (to.query.prepayGroup) {
    store.commit('styleQuizNav/SET_IS_PREPAY_FLOW', true)
    store.commit('styleQuizNav/SET_PREPAY_GROUP', to.query.prepayGroup)
  }

  if ((to.redirectedFrom?.name?.includes('stylistcase'))) {
    store.commit('case/SET_PREVIOUS_MY_CASE_FLYOUT_NAME', 'core')
    store.commit('case/SET_MY_CASE_FLYOUT_NAME', 'surprise-case')
  }


  if (to.matched.some(record => record.meta.requiresStyleProfile)) {
    if (!client.styleProfileComplete) {
      return ({ name: 'closet-sections' })
    }
  }
  let checksPassed = true

  // Check if unsubscribed required
  if (!allowAnonymous) {
    if (to.matched.some(record => record.meta.requiresUnsubscribed)) {
      const unsubscribedStates = ['non_member', 'former_member']
      checksPassed = checksPassed && (
        unsubscribedStates.includes(client.membershipStatus)
      )
    }
  }

  // Append client's UTM params if the route is eligible and they aren't already set
  const trackSharing = to.matched.some(record => record.meta.trackSharing)
  if (trackSharing && !hasUtmParams(to) && client?.referralCode !== null) {
    return ({
      ...to,
      query: {
        ...to.query,
        utm_source: 'armoire_member_share',
        utm_medium: 'organic',
        utm_campaign: client.referralCode
      }
    })
  }

  if (!checksPassed) {
    let next = ''
    from.name === null
      ? next = ({ name: 'closet-sections' })
      : next = ({ name: from.name })
    return next
  }
})

const originalPush = router.push
router.push = function push (location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch((err) => {
    if (isNavigationFailure(err, NavigationFailureType.redirected) ||
      isNavigationFailure(err, NavigationFailureType.duplicated)) {
      // resolve err
      return err
    }
    // rethrow error
    return Promise.reject(err)
  })
}
export default router
